import React, { useState } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Tabs from '../Tabs';
import OccurrenceChips from './attendance/OccurrenceChips';
import AttendanceTab from './attendance/AttendanceTab';
import LessonNotesTab from './attendance/LessonNotesTab';
import HistoryTab from './attendance/HistoryTab';
import LinkedResources from '../resources/LinkedResources';
import useAttendanceForm from '../../hooks/useAttendanceForm';
import { useStudents } from '../../data/useStudents';
import useNames from '../../hooks/useNames';
import { useAuth } from '../../context/AuthContext';
import s from './attendance/attendance.module.css';

// Taking attendance (SPEC.md §2.3).
//
// Assembly — the status, price and consequence sentence are useAttendanceForm,
// and each tab renders itself.
//
// Save sends nothing (#732). MMS's Save also sends the receipt, the lesson
// notes and the cancellation notices the drawer holds; here each message is
// sent from its own button beside its controls, which saves first. A knowing
// difference, argued in useAttendanceForm.js.

const TABS = ['Attendance', 'Lesson Details', 'History'];

export default function AttendanceDrawer({ open, onClose, occurrence, studentId }) {
  const { getStudent } = useStudents();
  const names = useNames();
  // For the History tab only, and never rendered: it compares this against the
  // record's `updatedBy` so it can say "you" instead of printing a uid (#202).
  const { user } = useAuth();
  const [tab, setTab] = useState(0);

  const student = studentId ? getStudent(studentId) : null;
  const form = useAttendanceForm({ occurrence, student, onSaved: onClose });

  if (!occurrence) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={student ? names.student(student) : 'Attendance'}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <FooterSpacer />
          <Button onClick={form.save} loading={form.saving && !form.sendingKind} disabled={form.saving}>Save</Button>
        </>
      )}
    >
      {form.saveError ? (
        <div className={s.error}>Could not save attendance: {form.saveError}</div>
      ) : null}

      <OccurrenceChips occurrence={occurrence} />

      <Tabs tabs={TABS} current={tab} onChange={setTab} />

      {tab === 0 ? (
        <AttendanceTab
          status={form.status}
          onStatusChange={form.setStatus}
          statuses={form.statuses}
          consequence={form.consequence}
          showPrice={form.showPrice}
          priceText={form.priceText}
          onPriceChange={form.setPriceText}
          priceError={form.priceError}
          paidAtLesson={form.paidAtLesson}
          onPaidChange={form.setPaidAtLesson}
          paidText={form.paidText}
          onPaidTextChange={form.setPaidText}
          paidError={form.paidError}
          paymentNote={form.paymentNote}
          onPaymentNoteChange={form.setPaymentNote}
          contacts={form.contacts}
          me={form.me}
          receiptTo={form.receiptTo}
          onReceiptToChange={form.setReceiptTo}
          receiptCcMe={form.receiptCcMe}
          onReceiptCcMeChange={form.setReceiptCcMe}
          showCancellationNotices={form.showCancellationNotices}
          noticeAlreadyQueued={form.noticeAlreadyQueued}
          notices={form.notices}
          noticeGroups={form.noticeGroups}
          onToggleNoticeChannel={form.toggleNoticeChannel}
          cancellationNote={form.cancellationNote}
          onCancellationNoteChange={form.setCancellationNote}
          reminderWarning={form.reminderWarning}
          sends={form.sends}
          onSend={form.send}
          noticeQueuedText={form.noticeQueuedText}
        />
      ) : null}

      {tab === 1 ? (
        <LessonNotesTab
          audience={form.noteAudience}
          onAudienceChange={form.setNoteAudience}
          notes={form.notes}
          onNoteChange={form.setNote}
          recipients={form.notesRecipients}
          notesTo={form.notesTo}
          onToggleNotesTo={form.toggleNotesTo}
          send={form.sends.notes}
          onSend={() => form.send('notes')}
          linkedResources={(
            <LinkedResources ids={form.resourceIds} onLink={form.linkResources} onUnlink={form.unlinkResource} />
          )}
        />
      ) : null}

      {tab === 2 ? <HistoryTab record={form.record} viewerUid={user?.uid || null} /> : null}
    </Drawer>
  );
}
